import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import ArticleSocialBlock from "../components/ArticleSocialBlock";
import ArticleAd from "../ads/ArticleAd";
import { seoConfig } from "../data/seoConfig";

const budgetLevels = [
  {
    level: "Very low budget",
    range: "USD 40 to 90",
    build: "Two-wheel line-following or obstacle-avoidance robot.",
    hardware: "ESP32-C3 or Arduino-compatible board, two geared DC motors, a TB6612FNG if current stays within its range, an ultrasonic sensor or line sensors and a simple acrylic or 3D-printed chassis.",
    limits: "No practical mapping or useful vision. Torque and runtime are limited. Reliability depends mainly on wiring and power delivery."
  },
  {
    level: "Serious beginner budget",
    range: "USD 120 to 280",
    build: "Mobile robot with encoders, an optional USB camera, more stable obstacle avoidance and basic odometry.",
    hardware: "ESP32 for motor control, Raspberry Pi Zero 2 W or Raspberry Pi 5 depending on local availability, DC motors with encoders, a TB6612FNG or DRV8833 driver, an IMU, a 5 V converter and a protected battery pack.",
    limits: "Navigation remains fragile without LiDAR. The camera needs consistent lighting. ROS 2 can run, but setup and tuning may take time."
  },
  {
    level: "Advanced but still limited budget",
    range: "USD 300 to 700",
    build: "Basic ROS 2 robot with a camera, encoders, an IMU and an entry-level 2D LiDAR when the budget allows it.",
    hardware: "Raspberry Pi 5 or Jetson Orin Nano Super for vision and edge AI, motors with encoders, a driver sized for the measured current, a 2D LiDAR such as the RPLIDAR A1 or YDLIDAR X2, a rigid chassis and a physical emergency stop.",
    limits: "This is not an industrial robot. Mapping can fail on glossy floors, in cluttered rooms, under strong light or when the wheels slip."
  }
];

const robotTypes = [
  {
    name: "Simple mobile robot",
    text: "This is the strongest first project. Two drive wheels, one caster, a microcontroller, a motor driver and one sensor are enough to learn motor control, power delivery and testing."
  },
  {
    name: "Wheeled robot",
    text: "This is the most practical starting point. Wheels avoid gait, balance and joint-torque problems. A wheeled chassis can later accept a camera, encoders and LiDAR."
  },
  {
    name: "Small robotic arm",
    text: "It teaches kinematics and servo control. The project becomes difficult as soon as the arm must carry a load. Mechanical backlash, plastic flex and weak servos limit accuracy."
  },
  {
    name: "Quadruped robot",
    text: "It requires several powerful servos, an IMU, a rigid frame, a stable gait and a power system that can handle current spikes. It is not the best first robot."
  },
  {
    name: "Humanoid",
    text: "A complete humanoid is not realistic on a small beginner budget. Bipedal walking requires dynamic balance, powerful actuators, reliable sensors, mechanical safety and extensive testing. A beginner can build a torso, hand or expressive head, but not a dependable full humanoid."
  }
];

const components = [
  {
    title: "Chassis",
    text: "Start with an acrylic plate, thin plywood, a simple 3D print or a two-wheel kit. The chassis must keep the wheels parallel. A twisted frame creates a tracking error that software cannot cleanly correct."
  },
  {
    title: "Microcontroller",
    text: "Arduino simplifies input/output, PWM and early tests. ESP32 adds Wi-Fi, Bluetooth and more processing power at a low price. The microcontroller should handle time-sensitive motor control."
  },
  {
    title: "Single-board computer",
    text: "Raspberry Pi supports Linux, Python, OpenCV, cameras and ROS 2. Jetson Orin Nano Super can run heavier AI models, but NVIDIA lists the developer kit from USD 249 before accessories. For a first robot, buy a single-board computer only when the camera or ROS 2 actually requires it."
  },
  {
    title: "Motors",
    text: "Geared DC motors are the simplest choice for a wheeled robot. Servomotors work well for a lightweight arm. Stepper motors provide open-loop positioning but draw more power and do not replace a true servo with position feedback."
  },
  {
    title: "Motor drivers",
    text: "The driver separates logic signals from motor current. The TB6612FNG suits small DC motors when current remains within its rated range. The L298N still appears in many kits, but ST’s data sheet shows substantial voltage drop. It heats quickly and performs poorly in low-voltage robots."
  },
  {
    title: "Battery",
    text: "Li-ion and LiPo packs store substantial energy in a small volume. They require the correct charger, deep-discharge and short-circuit protection and a mount that prevents crushing. A USB power bank may power the computer, but it may not handle the motors."
  },
  {
    title: "Sensors",
    text: "An HC-SR04 is enough to learn distance sensing, but it struggles with soft or angled surfaces. An IMU helps measure rotation and tilt. Wheel encoders matter more than many expensive sensors because they show whether the robot actually moved."
  },
  {
    title: "Camera",
    text: "A USB camera becomes useful when the robot must follow a color, recognize a simple object or stream video. It is not required at the beginning. Vision consumes processing power, depends on lighting and complicates troubleshooting."
  },
  {
    title: "Wheels or legs",
    text: "Wheels are the better choice for a first robot. Legs cost more, break sooner and require dynamic control. A wheeled robot keeps the budget focused on power, motors, encoders and software."
  },
  {
    title: "Power system",
    text: "Separate motor power from logic power. Use a stable 5 V converter for the controller. Connect a common ground between the controller and driver. Many beginner robots reboot because motor startup pulls the voltage down."
  },
  {
    title: "Wiring",
    text: "Use connectors that lock securely. Add strain relief with cable ties. Stop using Dupont wires for motors after the prototype stage. An intermittent connection looks like a software bug and can waste hours."
  },
  {
    title: "Software",
    text: "Arduino IDE is enough for the first tests. Python is practical on Raspberry Pi. ROS 2 becomes useful when several sensors and programs must exchange messages between perception, control and navigation. It also adds complexity."
  }
];

const buildSteps = [
  "Choose one simple mission. A useful first goal is to follow a black line, avoid an obstacle or travel one meter and return.",
  "Sketch the robot on paper. Place the battery, motors, wheels, controller, driver and switch before drilling the chassis.",
  "Test each motor before installing it. Measure no-load current and check driver temperature after several minutes.",
  "Assemble the chassis without sensors. Verify that the robot drives straight under a fixed command.",
  "Add sensors one at a time. Test the ultrasonic sensor, IMU and encoders with separate programs before combining them.",
  "Write simple motor control. Use PWM for speed, an H-bridge for direction and an immediate stop when a sensor returns an unsafe value.",
  "Close the control loop with encoders. The robot should correct the speed difference between the left and right wheels.",
  "Add basic navigation line following, obstacle avoidance or encoder-based distance travel.",
  "Log each test. Record battery voltage, runtime, driver temperature, path error and the problem observed.",
  "Upgrade only after measuring the problem. Change the motor, driver or battery for a documented reason, not because a video looks more impressive."
];

const mistakes = [
  "Buying a camera before the robot can drive straight.",
  "Powering the motors and microcontroller from a small 5 V output that cannot handle current spikes.",
  "Choosing motors without knowing their torque, speed and stall current.",
  "Using an L298N in a low-voltage robot and blaming the code when the motors are weak.",
  "Mounting the ultrasonic sensor too low, too high or behind part of the chassis.",
  "Building a chassis so light that it vibrates when the motors accelerate.",
  "Combining all the code without testing each subsystem separately.",
  "Charging a LiPo battery with an incompatible charger.",
  "Forgetting the main switch and having to unplug the battery by hand.",
  "Keeping no spare screws, wires, wheels or connectors."
];

const upgrades = [
  {
    title: "Level 1",
    text: "Move from open-loop travel to encoders. The robot stops guessing distance and starts measuring it."
  },
  {
    title: "Level 2",
    text: "Add an IMU to detect rotation, impacts, tilt and vibration. It does not replace encoders, but it improves diagnostics."
  },
  {
    title: "Level 3",
    text: "Add a USB camera only when the mission requires an image. Start by tracking a color or detecting a simple marker."
  },
  {
    title: "Level 4",
    text: "Move to ROS 2 when the number of programs becomes difficult to manage. Keep motor control on the microcontroller and use the Linux board for perception and decisions."
  },
  {
    title: "Level 5",
    text: "Add a 2D LiDAR when you need to map a room. Entry-level units such as the RPLIDAR A1 or YDLIDAR X2 are mainly useful indoors."
  }
];

const sources = [
  {
    label: "Arduino UNO R4 Minima",
    href: "https://store.arduino.cc/products/uno-r4-minima",
    detail: "Official listed price checked EUR 22. Renesas RA4M1 Arm Cortex-M4 microcontroller."
  },
  {
    label: "Espressif ESP32 C3 DevKitM 1",
    href: "https://docs.espressif.com/projects/esp-dev-kits/en/latest/esp32c3/esp32-c3-devkitm-1/index.html",
    detail: "Entry-level board with Wi-Fi and Bluetooth Low Energy."
  },
  {
    label: "DigiKey ESP32 DevKitC VE",
    href: "https://www.digikey.com/en/products/detail/espressif-systems/ESP32-DEVKITC-VE/12091812",
    detail: "Distributor price checked about USD 11 before tax and shipping."
  },
  {
    label: "Raspberry Pi 5",
    href: "https://www.raspberrypi.com/products/raspberry-pi-5/",
    detail: "Linux board with an Arm Cortex-A76 CPU and recommended 5 V, 5 A power input."
  },
  {
    label: "Raspberry Pi price update 2026",
    href: "https://www.raspberrypi.com/news/a-new-3gb-raspberry-pi-4-for-83-75-and-more-memory-driven-price-increases/",
    detail: "Raspberry Pi announced price increases tied to memory costs in 2026."
  },
  {
    label: "NVIDIA Jetson Orin Nano Super Developer Kit",
    href: "https://www.nvidia.com/en-us/autonomous-machines/embedded-systems/jetson-orin/nano-super-developer-kit/",
    detail: "NVIDIA listed the developer kit at USD 249."
  },
  {
    label: "Pololu TB6612FNG Dual Motor Driver Carrier",
    href: "https://www.pololu.com/product/713",
    detail: "Pololu price checked USD 4.95. Driver for two DC motors or one bipolar stepper motor."
  },
  {
    label: "STMicroelectronics L298",
    href: "https://www.st.com/en/motor-drivers/l298.html",
    detail: "Older dual H-bridge driver for inductive loads. Its data sheet shows a substantial voltage drop."
  },
  {
    label: "SparkFun HC SR04",
    href: "https://www.sparkfun.com/ultrasonic-distance-sensor-hc-sr04.html",
    detail: "SparkFun price checked USD 5.25 for a 5 V ultrasonic sensor."
  },
  {
    label: "Adafruit MPU 6050",
    href: "https://www.adafruit.com/product/3886",
    detail: "Adafruit price checked USD 12.95 for a six-axis IMU."
  },
  {
    label: "Adafruit Li ion and LiPoly batteries",
    href: "https://learn.adafruit.com/li-ion-and-lipoly-batteries/protection-circuitry",
    detail: "Guide to protection circuits, charging and lithium-cell risks."
  },
  {
    label: "ROS 2 Humble installation",
    href: "https://docs.ros.org/en/humble/Installation/Ubuntu-Install-Debs.html",
    detail: "Official ROS 2 documentation for Ubuntu 22.04."
  },
  {
    label: "RobotShop RPLIDAR A1M8",
    href: "https://eu.robotshop.com/products/rplidar-a1m8-360-degree-laser-scanner-development-kit",
    detail: "European price checked EUR 108.90 including tax for an entry-level 2D LiDAR."
  }
];

function Section({ eyebrow, title, children }) {
  return (
    <section className="soft-card p-6 shadow-sm sm:p-8">
      {eyebrow && <p className="text-xs font-bold uppercase tracking-[0.22em] text-neutral-500">{eyebrow}</p>}
      <h2 className="mt-3 text-3xl font-bold tracking-tight text-neutral-950">{title}</h2>
      <div className="mt-5 grid gap-4 text-base leading-8 text-neutral-700">{children}</div>
    </section>
  );
}

function CardGrid({ items }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {items.map((item) => (
        <article key={item.title || item.name} className="soft-card bg-neutral-50/90 p-5">
          <h3 className="text-lg font-black text-neutral-950">{item.title || item.name}</h3>
          <p className="mt-3 text-sm leading-7 text-neutral-700">{item.text}</p>
        </article>
      ))}
    </div>
  );
}

export default function BuildFirstRobotLimitedBudget() {
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robotics News", to: "/robotics-news" },
    { label: "First Robot on a Limited Budget", to: "/robotics-news/build-first-robot-limited-budget" }
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "How to Build Your First Robot from Scratch on a Limited Budget",
    description: "A practical TechniaHQRobot guide to building a functional first robot on a limited budget, with realistic components, engineering tradeoffs, safety, software and a construction plan.",
    author: { "@type": "Organization", name: "TechniaHQRobot" },
    publisher: { "@type": "Organization", name: "TechniaHQRobot" },
    datePublished: "2026-06-29",
    dateModified: "2026-06-29",
    mainEntityOfPage: `${seoConfig.baseUrl}/robotics-news/build-first-robot-limited-budget`
  };

  return (
    <div className="article-page">
      <SEO
        title="Build Your First Robot on a Budget"
        description="Build a first robot with a limited budget choose motors, drivers, sensors, battery, controller, software, safety checks and realistic limits."
        path="/robotics-news/build-first-robot-limited-budget"
        type="article"
        schema={[buildBreadcrumbSchema(breadcrumbs), articleSchema]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="DIY robotics guide"
        title="How to Build Your First Robot from Scratch on a Limited Budget"
        description="A first robot should move, measure, react and survive repeated testing. It does not need a laboratory. It needs one clear goal, reliable power, correctly sized motors and a strict test plan."
      />

      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-12 sm:px-6 lg:px-8">
        <section className="rounded-[2rem] border border-neutral-200 bg-neutral-950 p-6 text-white sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-neutral-400">Core Idea</p>
          <p className="mt-4 text-xl font-bold leading-8">
            The beginner’s trap is not a lack of money. It is trying to build too much before mastering power, motion, sensors and troubleshooting.
          </p>
          <p className="mt-4 text-sm leading-7 text-neutral-300">
            The prices shown are approximate figures checked with established manufacturers or distributors. They vary by country, tax, shipping, stock and supplier. A clone may cost less than an official board, while the official board often provides better documentation, more consistent quality and fewer lost hours.
          </p>
        </section>

        <ArticleAd placement="top" articleSlug="build-first-robot-limited-budget" />

        <Section title="Why Starting Small Is Smarter">
          <p>
            A robot is a physical machine. Software cannot compensate for a weak battery, a misaligned wheel, an undersized motor or an overheating driver. A small wheeled robot exposes the real constraints of robotics immediately.
          </p>
          <p>
            Starting small makes every problem measurable. The robot moves too slowly. Voltage drops when the motors start. The ultrasonic sensor produces unstable readings. A motor wire comes loose. Larger machines face the same problems, but the failures cost more and create greater risk.
          </p>
          <p>
            A good first goal is a robot that completes one visible, repeatable task. Following a line for two minutes without leaving the course is more valuable than a chassis packed with parts that do not work together.
          </p>
        </Section>

        <Section title="Which Robot Type to Choose for a First Project">
          <CardGrid items={robotTypes} />
        </Section>

        <Section title="Essential Components">
          <CardGrid items={components} />
        </Section>

        <Section title="A Realistic Minimum Budget">
          <div className="overflow-hidden rounded-3xl border border-neutral-200">
            <div className="grid bg-neutral-950 px-4 py-3 table-heading text-white md:grid-cols-[0.8fr_0.7fr_1.2fr_1.4fr_1.3fr]">
              <span>Level</span>
              <span>Budget</span>
              <span>Robot You Can Build</span>
              <span>Typical Hardware</span>
              <span>Limits</span>
            </div>
            {budgetLevels.map((item) => (
              <div key={item.level} className="grid gap-3 border-t border-neutral-200 bg-white px-4 py-5 text-sm leading-7 text-neutral-700 md:grid-cols-[0.8fr_0.7fr_1.2fr_1.4fr_1.3fr]">
                <strong className="text-neutral-950">{item.level}</strong>
                <span>{item.range}</span>
                <span>{item.build}</span>
                <span>{item.hardware}</span>
                <span>{item.limits}</span>
              </div>
            ))}
          </div>
          <p>
            This table excludes the programming computer, tools, outsourced 3D printing, taxes and shipping. To reduce cost, reuse an old chassis, buy fewer sensors and reserve the budget for motors, drivers, the battery and a multimeter.
          </p>
        </Section>

        <ArticleAd index={2} totalSections={8} articleSlug="build-first-robot-limited-budget" />

        <Section title="Skills You Need">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-3xl bg-neutral-50 p-5">
              <h3 className="font-black text-neutral-950">Practical Electrical Skills</h3>
              <p className="mt-3 text-sm leading-7">Measure voltage, current, polarity, common ground, battery capacity and motor stall current. Without these basics, the robot will reboot or burn out a driver.</p>
            </div>
            <div className="rounded-3xl bg-neutral-50 p-5">
              <h3 className="font-black text-neutral-950">Motor Control</h3>
              <p className="mt-3 text-sm leading-7">Understand PWM, direction, braking, H-bridges, acceleration ramps and encoders. A robot that starts abruptly can slip and lose odometry.</p>
            </div>
            <div className="rounded-3xl bg-neutral-50 p-5">
              <h3 className="font-black text-neutral-950">Embedded Programming</h3>
              <p className="mt-3 text-sm leading-7">Write short tests, read one sensor, filter a measurement, avoid blocking delays and log values during each run.</p>
            </div>
            <div className="rounded-3xl bg-neutral-50 p-5">
              <h3 className="font-black text-neutral-950">Basic Mechanics</h3>
              <p className="mt-3 text-sm leading-7">Align the wheels, tighten the fasteners, reduce flex, protect the wires and keep the battery accessible without dismantling the entire robot.</p>
            </div>
          </div>
        </Section>

        <Section title="Step-by-Step Build Plan">
          <div className="grid gap-3">
            {buildSteps.map((step, index) => (
              <div key={step} className="grid gap-3 soft-card bg-neutral-50/90 p-5 sm:grid-cols-[3rem_1fr]">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-950 text-sm font-black text-white">{index + 1}</span>
                <p className="text-sm leading-7 text-neutral-700">{step}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Motor Control and Basic Navigation">
          <p>
            Motor control starts with a stable PWM command. A PWM value is not the robot’s actual speed. It only defines how long the driver powers the motor during each cycle. Real speed depends on the battery, load, floor and motor.
          </p>
          <p>
            Encoders address this limitation by counting rotation. With two encoders, the robot can estimate distance and correct a slower wheel. Odometry still becomes inaccurate when the wheels slip, the floor is slick or the chassis vibrates.
          </p>
          <p>
            Line following is the simplest navigation task. It requires reflectance sensors and a feedback loop. Ultrasonic obstacle avoidance is useful for learning, but it does not understand the scene. A 2D LiDAR improves indoor mapping, but it does not turn a beginner project into an industrial robot.
          </p>
        </Section>

        <Section title="Safety, Testing and Maintenance">
          <p>
            Add an accessible main switch. Add a fuse or protection circuit when the pack can deliver high current. Never charge a lithium battery unattended. Use a charger matched to the battery chemistry and cell count. Verify polarity before connecting power.
          </p>
          <p>
            Run the first test with the wheels raised. Run the second on the floor at low speed. Use the third to measure runtime and temperature. Stop immediately if a driver overheats, a cable is loose or the battery swells.
          </p>
          <p>
            Maintenance on a low-cost robot is simple but mandatory retighten screws, inspect wires, clean the wheels, check connectors, inspect the battery and back up the code after every stable version.
          </p>
        </Section>

        <Section title="Mistakes That Waste Money">
          <div className="grid gap-3 md:grid-cols-2">
            {mistakes.map((mistake) => (
              <p key={mistake} className="rounded-2xl border border-neutral-200/80 bg-neutral-50 p-4 text-sm leading-7 text-neutral-700">{mistake}</p>
            ))}
          </div>
        </Section>

        <Section title="Limits of a Low-Cost Robot">
          <p>
            A low-cost robot is excellent for learning, but it is not as robust as an industrial AMR. It has no safety certification, environmental protection, sensor redundancy or advanced field diagnostics.
          </p>
          <p>
            Mechanical accuracy remains limited. Wheels can slip. Inexpensive motors have inconsistent tolerances. Hobby servos heat up and develop backlash. Low-cost sensors produce noisy measurements. Camera performance degrades as lighting changes.
          </p>
          <p>
            Integration is the largest constraint. A component can work well alone and fail inside the complete robot. A powerful Raspberry Pi is useless if the battery cuts out. An expensive LiDAR is wasted if the wheels have no encoders. Clean code cannot rescue a misaligned chassis.
          </p>
        </Section>

        <Section title="How to Upgrade the Robot Gradually">
          <CardGrid items={upgrades} />
        </Section>

        <Section title="Practical Conclusion">
          <p>
            The best first robot is the one you can understand, measure, repair and improve. Start with wheels and give the machine one simple mission. Spend the budget on power, motors, the driver and testing. Robotics becomes serious when the robot repeats a real action without anyone touching the wiring between runs.
          </p>
        </Section>

        <ArticleAd placement="bottom" articleSlug="build-first-robot-limited-budget" />

        <ArticleSocialBlock />

        <section className="soft-card bg-neutral-50/90 p-6 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-neutral-500">Verified Sources</p>
          <div className="mt-5 grid gap-3">
            {sources.map((source) => (
              <a key={source.href} href={source.href} target="_blank" rel="noreferrer" className="rounded-2xl border border-neutral-200 bg-white p-4 transition hover:border-neutral-950">
                <span className="block text-sm font-black text-neutral-950">{source.label}</span>
                <span className="mt-1 block text-sm leading-6 text-neutral-600">{source.detail}</span>
              </a>
            ))}
          </div>
        </section>
      </div>

      <CTASection title="Follow TechniaHQRobot for practical robotics guides" />
    </div>
  );
}
